import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
  {
    q: "How much does a personal trainer in Northwood cost?",
    a: "One-to-one personal training with Harry starts at £330 per month for one 1-hour session per week, or £606 per month for two sessions per week. I also offer single drop-in sessions at £80 and custom group sessions priced on application. Every plan begins with a free 30-minute consultation.",
  },
  {
    q: "Do you offer strength training for beginners and older adults?",
    a: "Absolutely. Strength training for every body is what I'm built on — from complete beginners to clients in their 70s and 80s. Programmes focus on safe technique, balance, bone health and confidence, all scaled to your starting point.",
  },
  {
    q: "Can you help with injury rehabilitation and back pain?",
    a: "Yes. Rehabilitation and better movement are a core part of what I do. I start with a one-to-one movement assessment and build a programme around your injury history — whether that's back pain, a knee injury, or returning to exercise after surgery. I work alongside, not instead of, your physio or doctor.",
  },
  {
    q: "Do you train athletes and sports performance clients?",
    a: "Yes — alongside my general population clients, I coach athletes in strength, speed, power and conditioning, with progress measured week by week so you peak when it counts.",
  },
  {
    q: "Where is Harry based?",
    a: "I train clients at OSHPerformance, a private personal training gym at 32 High Street, Northwood HA6 1BN — easy to reach from Ruislip, Pinner, Harrow, Watford, Moor Park and across North West London and Hertfordshire.",
  },
  {
    q: "Do you offer group or couples training sessions?",
    a: "Yes — I run custom group sessions for couples, friends, families, teams and workplaces. Sessions are tailored to the group's size and goals, and priced on application. Get in touch for a quote.",
  },
  {
    q: "What happens in the free consultation?",
    a: "A relaxed 30-minute chat at the gym or over the phone. We talk through your goals, injury history and lifestyle, I look at how you move, and we map out what training with me would look like. No pressure, no obligation — just an honest plan.",
  },
];

export const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function FAQ() {
  return (
    <section className="py-24 md:py-40 border-b border-white/10" data-testid="faq-section">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-14 md:mb-20 flex flex-col md:flex-row md:items-end justify-between gap-6"
        >
          <h2 className="font-display font-bold uppercase leading-[0.9] text-5xl md:text-7xl" data-testid="faq-heading">
            Common <span className="text-gold italic">Questions</span>
          </h2>
          <p className="max-w-sm text-zinc-400 text-base md:text-lg">
            Everything people usually ask before starting personal training in Northwood.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="max-w-4xl"
        >
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
                <AccordionTrigger
                  data-testid={`faq-question-${i}`}
                  className="font-display font-semibold uppercase tracking-wide text-lg md:text-xl text-left text-white hover:text-gold hover:no-underline py-6 transition-colors duration-300"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-zinc-400 text-base leading-relaxed pb-6" data-testid={`faq-answer-${i}`}>
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}
